import { styled } from '@mui/material/styles'
import Box from '@/components/common/Box'
import {
	colors,
	typePresets,
	spacing,
	radius,
	shadows,
	sizing,
	zIndex,
} from '@/styles/designTokens'

/** Full-viewport dimmer backdrop — not exported; only consumed as base for BackdropDesktop */
const Backdrop = styled(Box)({
	position: 'fixed',
	inset: 0,
	backgroundColor: colors.overlayBackdrop,
	display: 'flex',
	alignItems: 'flex-end',
	justifyContent: 'center',
	zIndex: zIndex.storeClosedOverlay,
})

/** Desktop: centre the card vertically; Mobile: pin to bottom (default Backdrop alignment) */
export const BackdropDesktop = styled(Backdrop)(({ theme }) => ({
	[theme.breakpoints.up('md')]: {
		alignItems: 'center',
	},
}))

/** The white card / sheet container */
export const Card = styled(Box)(({ theme }) => ({
	backgroundColor: colors.surfaceWhite,
	borderRadius: `${radius.sheet}px ${radius.sheet}px 0 0`,
	width: '100%',
	maxHeight: sizing.mobileSheetMaxHeight,
	overflow: 'auto',
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'center',
	padding: `${spacing.xl}px ${spacing.lg}px`,
	gap: spacing.lg,
	boxShadow: shadows.dropdown,
	[theme.breakpoints.up('md')]: {
		borderRadius: `${radius.sheet}px`,
		width: `${sizing.storeClosedModalWidth}px`,
		maxWidth: '100%',
		maxHeight: '90vh',
		boxShadow: shadows.modal,
		margin: `${spacing.lg}px`,
	},
}))

/** Circular restaurant logo */
export const LocationLogo = styled('img')({
	width: sizing.storeClosedLogoSize,
	height: sizing.storeClosedLogoSize,
	borderRadius: radius.circle,
	objectFit: 'cover',
	flexShrink: 0,
})

/** Groups location name and address — no gap between them, Figma spec */
export const LocationInfo = styled(Box)({
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'center',
	width: '100%',
})

/** Location name — bold heading */
export const LocationName = styled('h2')({
	...typePresets.headingXl,
	margin: 0,
	color: colors.textPrimary,
	textAlign: 'center',
	wordBreak: 'break-word',
})

/** Address line below the location name */
export const LocationAddress = styled('p')({
	...typePresets.bodyMd,
	margin: 0,
	color: colors.textSecondary,
	textAlign: 'center',
	wordBreak: 'break-word',
})

/** "Store Closed" status pill — error/light background, error/alt text */
export const StatusPill = styled(Box)({
	display: 'inline-flex',
	alignItems: 'center',
	gap: spacing.xs,
	padding: `${spacing.xs}px ${spacing.md}px`,
	borderRadius: radius.pill,
	backgroundColor: colors.errorAltLight,
	color: colors.errorAlt,
})

/** Text inside the status pill */
export const StatusPillText = styled('span')({
	...typePresets.labelMd,
	color: colors.errorAlt,
	whiteSpace: 'nowrap',
})

/** Body copy explaining that ordering is unavailable — no exact typePreset match (16px regular, not bold) */
export const MessageText = styled('p')({
	...typePresets.headingLg,
	fontWeight: 400,
	margin: 0,
	color: colors.textPrimary,
	textAlign: 'center',
	wordBreak: 'break-word',
})

/** Error code line shown below the message for POS_CONFIGURATION errors */
export const ErrorCodeText = styled('p')({
	...typePresets.bodySm,
	margin: 0,
	color: colors.textSecondary,
	textAlign: 'center',
})
